import React from 'react';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';

const { FiCheck, FiX, FiMinus } = FiIcons;

const ComparisonSection = () => {
  const comparisons = [
    {
      category: "Focus Area",
      weTB: "Executive function neuroscience",
      sportsP: "General mental skills", 
      mentalP: "Motivation & mindset",
      traditional: "Physical skills only"
    },
    {
      category: "Addresses Root Cause",
      weTB: "✅ Trains brain's performance systems",
      sportsP: "⚠️ Surface-level mental techniques",
      mentalP: "⚠️ Mindset focus only", 
      traditional: "❌ Ignores cognitive component"
    },
    {
      category: "Measurable Results", 
      weTB: "✅ 28% decision speed improvement",
      sportsP: "⚠️ Subjective improvements",
      mentalP: "⚠️ Hard to quantify",
      traditional: "✅ Physical metrics only"
    },
    {
      category: "Pressure Performance",
      weTB: "✅ 15% fewer errors under pressure", 
      sportsP: "⚠️ General confidence building",
      mentalP: "⚠️ Variable effectiveness",
      traditional: "❌ Often deteriorates"
    },
    {
      category: "Transfer to Competition",
      weTB: "✅ Direct cognitive skill application",
      sportsP: "⚠️ Mixed transfer",
      mentalP: "⚠️ Dependent on techniques", 
      traditional: "⚠️ Practice vs. game gap"
    },
    {
      category: "Long-term Development",
      weTB: "✅ Builds lasting brain infrastructure",
      sportsP: "⚠️ Requires ongoing sessions", 
      mentalP: "⚠️ Tool-dependent",
      traditional: "✅ Physical skills last"
    },
    {
      category: "Investment",
      weTB: "$135-200/session",
      sportsP: "$100-250/session",
      mentalP: "$150-300/session",
      traditional: "$50-150/session"
    },
    {
      category: "Elite Athlete Focus", 
      weTB: "✅ Designed for high performers and those who want to get there",
      sportsP: "⚠️ General population focus",
      mentalP: "✅ Elite-focused",
      traditional: "⚠️ Varies by coach"
    }
  ];

  const getIcon = (text) => {
    if (text.includes('✅')) return FiCheck;
    if (text.includes('❌')) return FiX;
    if (text.includes('⚠️')) return FiMinus;
    return null;
  };

  const getColor = (text) => {
    if (text.includes('✅')) return 'text-green-500';
    if (text.includes('❌')) return 'text-red-500'; 
    if (text.includes('⚠️')) return 'text-yellow-500';
    return 'text-gray-700';
  };

  const cleanText = (text) => {
    // Remove emoji characters using a more specific approach
    return text.replace(/✅\s?|❌\s?|⚠️\s?/g, '');
  };

  return (
    <section className="py-20 bg-gray-50">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mb-16"
        >
          <h2 className="font-playfair text-3xl lg:text-5xl font-bold text-gray-900 mb-6">
            We The Billions vs. Traditional Sports Performance Training
          </h2>
        </motion.div>

        {/* Comparison Table */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="bg-white rounded-2xl shadow-lg border border-gray-100 overflow-hidden"
        >
          {/* Desktop Table */}
          <div className="hidden lg:block overflow-x-auto">
            <table className="w-full">
              <thead>
                <tr className="bg-gradient-to-r from-accent-light/50 to-accent-purple/30">
                  <th className="px-6 py-4 text-left font-playfair text-lg font-bold text-gray-900">Comparison</th>
                  <th className="px-6 py-4 text-center font-playfair text-lg font-bold text-accent-magenta">We The Billions</th>
                  <th className="px-6 py-4 text-center font-playfair text-lg font-bold text-gray-700">Sports Psychology</th>
                  <th className="px-6 py-4 text-center font-playfair text-lg font-bold text-gray-700">Mental Performance Coaches</th>
                  <th className="px-6 py-4 text-center font-playfair text-lg font-bold text-gray-700">Traditional Training</th>
                </tr>
              </thead>
              <tbody>
                {comparisons.map((row, index) => (
                  <motion.tr
                    key={index}
                    initial={{ opacity: 0, x: -20 }}
                    whileInView={{ opacity: 1, x: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                    className="border-b border-gray-100 hover:bg-gray-50 transition-colors duration-200"
                  >
                    <td className="px-6 py-4 font-semibold text-gray-900">{row.category}</td>
                    <td className="px-6 py-4 text-center bg-accent-magenta/5">
                      <div className={`flex items-center justify-center gap-2 ${getColor(row.weTB)}`}>
                        {getIcon(row.weTB) && <SafeIcon icon={getIcon(row.weTB)} className="h-5 w-5" />}
                        <span className="font-medium">{cleanText(row.weTB)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className={`flex items-center justify-center gap-2 ${getColor(row.sportsP)}`}>
                        {getIcon(row.sportsP) && <SafeIcon icon={getIcon(row.sportsP)} className="h-5 w-5" />}
                        <span>{cleanText(row.sportsP)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className={`flex items-center justify-center gap-2 ${getColor(row.mentalP)}`}>
                        {getIcon(row.mentalP) && <SafeIcon icon={getIcon(row.mentalP)} className="h-5 w-5" />}
                        <span>{cleanText(row.mentalP)}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4 text-center">
                      <div className={`flex items-center justify-center gap-2 ${getColor(row.traditional)}`}>
                        {getIcon(row.traditional) && <SafeIcon icon={getIcon(row.traditional)} className="h-5 w-5" />}
                        <span>{cleanText(row.traditional)}</span>
                      </div>
                    </td>
                  </motion.tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Cards */}
          <div className="lg:hidden space-y-6 p-6">
            {comparisons.map((row, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5, delay: index * 0.1 }}
                className="bg-gray-50 rounded-xl p-6"
              >
                <h3 className="font-playfair text-lg font-bold text-gray-900 mb-4">{row.category}</h3>
                
                <div className="space-y-3">
                  <div className="bg-accent-magenta/10 p-3 rounded-lg">
                    <div className="font-semibold text-accent-magenta mb-1">We The Billions</div>
                    <div className={`flex items-center gap-2 ${getColor(row.weTB)}`}>
                      {getIcon(row.weTB) && <SafeIcon icon={getIcon(row.weTB)} className="h-4 w-4" />}
                      <span className="text-sm">{cleanText(row.weTB)}</span>
                    </div>
                  </div>
                  
                  <div className="grid grid-cols-1 gap-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-gray-600">Sports Psychology:</span>
                      <div className={`flex items-center gap-1 ${getColor(row.sportsP)}`}>
                        {getIcon(row.sportsP) && <SafeIcon icon={getIcon(row.sportsP)} className="h-4 w-4" />}
                        <span>{cleanText(row.sportsP)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Mental Performance:</span>
                      <div className={`flex items-center gap-1 ${getColor(row.mentalP)}`}>
                        {getIcon(row.mentalP) && <SafeIcon icon={getIcon(row.mentalP)} className="h-4 w-4" />}
                        <span>{cleanText(row.mentalP)}</span>
                      </div>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-600">Traditional:</span>
                      <div className={`flex items-center gap-1 ${getColor(row.traditional)}`}>
                        {getIcon(row.traditional) && <SafeIcon icon={getIcon(row.traditional)} className="h-4 w-4" />}
                        <span>{cleanText(row.traditional)}</span>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </motion.div>

        {/* CTA */}
        <motion.div
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.8 }}
          className="text-center mt-16"
        >
          <button
            onClick={() => {
              const element = document.getElementById('contact');
              if (element) element.scrollIntoView({ behavior: 'smooth' });
            }}
            className="btn-primary text-lg"
          >
            Choose the Science-Backed Approach
          </button>
        </motion.div>
      </div>
    </section>
  );
};

export default ComparisonSection;